import { PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID } from '@bariendo/lib/constants';
import { buildPacIntroCallSummary, PacIntroCallNoteAnswers } from '@bariendo/lib/pac-intro-call-note';
import { PAC_INTRO_CALL_SCRIPT } from '@bariendo/lib/pac-intro-call-script';
import { PacWidget } from '@bariendo/lib/patient-access-script';
import { Communication, Patient } from '@medplum/fhirtypes';
import { useCallback, useState } from 'react';
import { PatientAccessCallCard } from './PatientAccessCallCard';
import { PacIntroCallSlotGrid } from './PacIntroCallSlotGrid';
import { summarizePacIntroCallBeat } from './pacIntroCallSummary';
import { usePatientAccessActions } from './usePatientAccessActions';
import { usePatientAccessCallDraft } from './usePatientAccessCallDraft';
import { ConsultSlot, PN_CONSULT, consultSlotKey, useNpConsultSlots } from './useNpConsultSlots';

interface PacIntroCallCardProps {
  note: Communication;
  answers: PacIntroCallNoteAnswers;
  patient?: Patient;
  /**
   * A note already saved to the chart. Expanding it shows the worked script as a
   * record of the call; it is not an editor.
   */
  readOnly?: boolean;
  onSaved?: () => void;
}

/**
 * The PAC intro call: what a rep works while an inbound caller is on the line.
 *
 * Four sections — open, answer FAQs, book the consultation, internal notes — and
 * almost no state of its own, because almost nothing on this call is charted.
 * The card owns the one live thing it has: availability for the free
 * consultation, held while the rep keeps talking and booked when they save.
 *
 * Saving is not gated. The PN consultation blocks Save until an outcome is set,
 * because its outcome is what the ad-platform events fire from; nothing fires
 * from this one, so a rep who wants to chart a 40-second wrong-number is not
 * made to grade it first.
 */
export function PacIntroCallCard({
  note,
  answers: initialAnswers,
  patient,
  readOnly = false,
  onSaved,
}: PacIntroCallCardProps) {
  const { answers, updateAnswers, setValue, saveToChart, isSaving, saveError } =
    usePatientAccessCallDraft<PacIntroCallNoteAnswers>({
      note,
      initialAnswers,
      answersPayloadId: PAC_INTRO_CALL_ANSWERS_PAYLOAD_ID,
      buildSummary: buildPacIntroCallSummary,
      onSaved,
    });

  /** Availability is only queried while the booking section is on screen. */
  const [openBeat, setOpenBeat] = useState<string | null>(null);
  const slots = useNpConsultSlots(patient, openBeat === 'book', { inPatientTimeZone: true, consult: PN_CONSULT });
  const { handleAction, pendingAction } = usePatientAccessActions(patient);

  const handleSelectSlot = useCallback(
    async (slot: ConsultSlot) => {
      const previous = answers.slot;

      // Re-tapping the held slot clears it so the rep can pick another time.
      // Compared on the full identity: with several providers free at the same
      // time, matching on `start` made a tap on a different provider look like a
      // re-tap, so the card released the hold instead of switching to it.
      if (previous && consultSlotKey(previous) === consultSlotKey(slot)) {
        updateAnswers((current) => ({ ...current, slot: undefined }));
        await slots.release(previous);
        return;
      }

      const held = await slots.hold(slot);
      if (!held) return;

      updateAnswers((current) => ({ ...current, slot: held }));
      // Hold the new time before giving back the old one, so the rep never
      // drops their place in favour of a slot that turns out to be taken.
      await slots.release(previous);
    },
    [answers.slot, slots, updateAnswers]
  );

  /**
   * Book the held consultation on save, so the rep confirms it as part of
   * closing the call rather than as a separate step.
   *
   * A booking that fails — an expired hold, someone else taking the time — must
   * not trap the rep on an unsaveable card. The failed slot is dropped so the
   * note cannot claim a consultation that does not exist, the error is surfaced
   * by `book`, and saving again charts the call without a booking.
   */
  const handleSave = useCallback(async () => {
    if (answers.slot?.slotId) {
      const failedSlot = answers.slot;
      const booked = await slots.book(failedSlot);
      if (!booked) {
        updateAnswers((current) => ({ ...current, slot: undefined }));
        // The hold may still be standing if the booking failed for any reason
        // other than expiry — give it back rather than sitting on it.
        await slots.release(failedSlot);
        return;
      }
    }
    await saveToChart();
  }, [answers.slot, slots, saveToChart, updateAnswers]);

  /** The one widget kind this script has that the shared spine does not know. */
  const renderCardWidget = useCallback(
    (widget: PacWidget) => {
      if (widget.kind !== 'slotGrid') return null;
      return (
        <PacIntroCallSlotGrid
          slots={slots.slots}
          loading={slots.loading}
          timeZone={slots.timeZone}
          derivedTimeZone={slots.derivedTimeZone}
          onTimeZoneChange={slots.setTimeZone}
          dayOffset={slots.dayOffset}
          maxDayOffset={slots.maxDayOffset}
          onDayChange={slots.setDayOffset}
          selected={answers.slot}
          onSelect={handleSelectSlot}
          disabled={readOnly}
        />
      );
    },
    [slots, answers.slot, handleSelectSlot, readOnly]
  );

  const summarizeBeat = useCallback(
    (beatId: string, current: PacIntroCallNoteAnswers) => summarizePacIntroCallBeat(beatId, current, slots.timeZone),
    [slots.timeZone]
  );

  return (
    <PatientAccessCallCard<PacIntroCallNoteAnswers>
      script={PAC_INTRO_CALL_SCRIPT}
      answers={answers}
      setValue={setValue}
      summarizeBeat={summarizeBeat}
      onAction={handleAction}
      pendingAction={pendingAction}
      renderCardWidget={renderCardWidget}
      // Two of the four sections capture nothing — the opening is read and the
      // FAQs are looked up. A tick that can never light is noise.
      showBeatProgress={false}
      readOnly={readOnly}
      isSaving={isSaving || slots.isBooking}
      saveError={saveError}
      onSave={handleSave}
      onOpenBeatChange={setOpenBeat}
    />
  );
}
